/**
 * Events (bookings) data access for the admin SPA.
 *
 * Talks to the Go service via the shared api-client: `GET /api/admin/events`
 * (list + filters + the client dropdown, response shape
 * `{ bookings, total, clients }`) and `DELETE /api/admin/events` (bulk delete).
 */
import { apiFetch } from "../../../shared/api-client";
import {
  asBookingPhase,
  type BookingPhase,
  type PhaseFilter,
} from "./booking-phase";

/**
 * Sort modes. All but the first are columns, sorted in the requested `order`.
 *
 * `upcoming` is chronological order — the Event happening soonest at the top,
 * then forwards in time, with past Events after the future ones, most recent
 * first — and it ignores `order`. It is not a column, so no header can express
 * it; the events list reaches it through the sort picker.
 */
export const SORT_FIELDS = [
  "upcoming",
  "event_date",
  "created_at",
  "client_name",
  "phase_step",
] as const;
export type SortField = (typeof SORT_FIELDS)[number];
export type SortOrder = "asc" | "desc";

export function isSortField(value: string): value is SortField {
  return (SORT_FIELDS as readonly string[]).includes(value);
}

/** Normalised booking row used by the events list. */
export interface Booking {
  id: string;
  quote_number: string;
  event_name: string | null;
  event_date: string;
  event_end_date: string | null;
  event_time: string | null;
  location: string | null;
  client_name: string;
  client_email: string;
  client_phone: string;
  booking_type: string;
  inquiry_path:
    | "quick_enquiry"
    | "full_brief"
    | "dry_hire"
    | "phone_call"
    | null;
  preferred_contact_method: "email" | "phone" | "text" | null;
  request_summary: string | null;
  phase: BookingPhase;
  created_at: string;
  cancelled_at: string | null;
  quote_total: number | null;
  crew_count: number | null;
}

/** A unique client, for the filter dropdown. */
export interface Client {
  client_name: string;
  client_email: string;
}

export interface EventsResponse {
  bookings: Booking[];
  total: number;
  clients: Client[];
}

/** Filters/sort/pagination passed to the list endpoint. */
export interface EventsQuery {
  search?: string;
  phase?: PhaseFilter;
  client?: string;
  dateFrom?: string;
  dateTo?: string;
  sort: SortField;
  order: SortOrder;
  limit: number;
  offset: number;
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function strOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function numOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  // The Go service emits numerics as strings; coerce defensively.
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Coerce one raw row into a normalised Booking. Rows missing an id are dropped. */
function normalise(raw: unknown): Booking | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string") return null;
  // An unknown/absent phase falls back to "inquiry" so the badge always renders.
  const phase = asBookingPhase(r.phase) ?? "inquiry";
  return {
    id: r.id,
    quote_number: str(r.quote_number),
    event_name: strOrNull(r.event_name),
    event_date: str(r.event_date),
    event_end_date: strOrNull(r.event_end_date),
    event_time: strOrNull(r.event_time),
    location: strOrNull(r.location),
    client_name: str(r.client_name),
    client_email: str(r.client_email),
    client_phone: str(r.client_phone),
    booking_type: str(r.booking_type),
    inquiry_path:
      r.inquiry_path === "quick_enquiry" ||
      r.inquiry_path === "full_brief" ||
      r.inquiry_path === "dry_hire" ||
      r.inquiry_path === "phone_call"
        ? r.inquiry_path
        : null,
    preferred_contact_method:
      r.preferred_contact_method === "email" ||
      r.preferred_contact_method === "phone" ||
      r.preferred_contact_method === "text"
        ? r.preferred_contact_method
        : null,
    request_summary: strOrNull(r.request_summary),
    phase,
    created_at: str(r.created_at),
    cancelled_at: strOrNull(r.cancelled_at),
    quote_total: numOrNull(r.quote_total),
    crew_count: numOrNull(r.crew_count),
  };
}

function normaliseClient(raw: unknown): Client | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.client_email !== "string" || r.client_email === "") return null;
  return { client_name: str(r.client_name), client_email: r.client_email };
}

/** Delete one or more events by id. Returns the count of rows actually removed. */
export async function deleteEvents(
  ids: string[],
): Promise<{ deleted: number }> {
  const data = await apiFetch<{ deleted: number }>("/api/admin/events", {
    method: "DELETE",
    body: { ids },
  });
  return data;
}

/**
 * Fetch the events list from `GET /api/admin/events` (query params: `search`,
 * `phase`, `client`, `date_from`, `date_to`, `sort`, `order`, `limit`,
 * `offset`).
 */
export async function listEvents(
  q: EventsQuery,
  signal?: AbortSignal,
): Promise<EventsResponse> {
  const data = await apiFetch<unknown>("/api/admin/events", {
    query: {
      search: q.search || undefined,
      phase: q.phase || undefined,
      client: q.client || undefined,
      date_from: q.dateFrom || undefined,
      date_to: q.dateTo || undefined,
      sort: q.sort,
      order: q.order,
      limit: String(q.limit),
      offset: String(q.offset),
    },
    signal,
  });

  if (typeof data !== "object" || data === null) {
    throw new Error("Expected events response object");
  }
  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.bookings))
    throw new Error("Expected events.bookings array");

  const bookings = obj.bookings
    .map(normalise)
    .filter((b): b is Booking => b !== null);
  const clients = Array.isArray(obj.clients)
    ? obj.clients.map(normaliseClient).filter((c): c is Client => c !== null)
    : [];
  const total = typeof obj.total === "number" ? obj.total : bookings.length;

  return { bookings, total, clients };
}
